// Known-flag validation for the `context` command group.
//
// `parseArgs` accepts ANY `--foo` and silently drops what no handler reads, so a
// typo (`--gotchs`) or an unsupported flag vanishes and the command still
// reports success — a silent lie. This module is the allowlist + guard that
// turns that silence into a clear error.
//
// Design: ONE union allowlist for the whole `context` group (not per-subcommand).
// A flag valid for ANY context subcommand passes everywhere. That deliberately
// tolerates a real flag on the "wrong" subcommand (e.g. `--depth` on `add`),
// trading a little precision for near-zero false-positive risk — the goal is to
// catch typos/garbage, and breaking a valid invocation is far worse than letting
// a harmless misplaced flag through. The set below is the exhaustive union of
// every flag any context handler reads (grep `flags[` / `collectMulti`), plus
// `reason` which is documented in `reject` usage even though it's not yet wired.

export const KNOWN_CONTEXT_FLAGS: ReadonlySet<string> = new Set([
  // description / fields
  'what', 'how', 'decisions', 'gotchas', 'ownership', 'content', 'title', 'tags', 'status',
  // append forms
  'gotcha', 'decision', 'invariant', 'check', 'gotchas_append',
  // replace forms for array fields
  'invariants', 'checks',
  // anchors
  'pattern', 'add-pattern', 'remove-pattern', 'where', 'file', 'files',
  // relations
  'rel',
  // read / list / search / retrieve
  'all', 'limit', 'cursor', 'stale', 'docs', 'doc', 'manual', 'auto', 'suggested', 'repo',
  'include-notes', 'diff', 'mark', 'depth', 'note', 'tag', 'type', 'view', 'explain',
  // governance / pr
  'reason', 'open', 'summary', 'merge', 'reject',
  // workspace / share / area
  'to', 'revoke', 'with', 'read', 'area',
  // enforcement
  'enforce',
  // secret hygiene — override the secret-scan and write anyway
  'allow-secrets',
  // ARP v2 — typed external anchors + structured rules; --type external = Directory
  'anchor', 'rule', 'type',
  // global
  'json', 'help', 'dry-run',
])

// Classic Levenshtein distance — small inputs (flag names), so the O(n*m) DP is
// fine. Used only to suggest the nearest known flag on a typo.
export function levenshtein(a: string, b: string): number {
  const m = a.length, n = b.length
  if (m === 0) return n
  if (n === 0) return m
  let prev = Array.from({ length: n + 1 }, (_, j) => j)
  let cur = new Array<number>(n + 1)
  for (let i = 1; i <= m; i++) {
    cur[0] = i
    for (let j = 1; j <= n; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1
      cur[j] = Math.min(prev[j] + 1, cur[j - 1] + 1, prev[j - 1] + cost)
    }
    ;[prev, cur] = [cur, prev]
  }
  return prev[n]
}

// Nearest known flag (from any flag set) within a small edit distance, or null
// if nothing is close. Shared by every command's unknown-flag handler so the
// suggestion heuristic lives in ONE place instead of being copy-pasted per group.
export function suggestFromSet(unknown: string, known: Iterable<string>): string | null {
  let best: string | null = null
  let bestDist = Infinity
  for (const candidate of known) {
    const d = levenshtein(unknown, candidate)
    if (d < bestDist) { bestDist = d; best = candidate }
  }
  // Only suggest when it's genuinely close — avoid "did you mean --to?" for a
  // wildly different flag. Allow distance up to ~half the flag length.
  const threshold = Math.max(2, Math.floor(unknown.length / 2))
  return best !== null && bestDist <= threshold ? best : null
}

// Nearest known context flag — the context command's specialization.
export function suggestFlag(unknown: string): string | null {
  return suggestFromSet(unknown, KNOWN_CONTEXT_FLAGS)
}

// Throws (via process.exit unless `onError` provided) on the first batch of
// unknown flags. `onError` is injectable so tests can assert without exiting.
export function assertKnownFlags(
  flags: Record<string, string | boolean>,
  onError: (msg: string) => never = (msg) => { console.error(msg); process.exit(1) },
): void {
  const unknown = Object.keys(flags).filter((f) => !KNOWN_CONTEXT_FLAGS.has(f))
  if (unknown.length === 0) return
  const lines = unknown.map((f) => {
    const hint = suggestFlag(f)
    return `  --${f}${hint ? `  (did you mean --${hint}?)` : ''}`
  })
  onError(
    `Error: unknown flag${unknown.length > 1 ? 's' : ''} for 'driftless context':\n` +
      lines.join('\n') +
      `\nRun 'driftless context <subcommand> --help' for valid flags.`,
  )
}
